// Minimal reader for Excel Binary Workbooks (.xlsb), no dependencies.
// An .xlsb is a ZIP of binary record streams (MS-XLSB). We read sheet names,
// the shared string table and cell values (text, numbers, booleans) - enough
// to import master / stock lists. Formatting and formulas are ignored (the
// cached formula result is used).

import { openZip } from './zip.js';

const utf16 = new TextDecoder('utf-16le');
const decoder = new TextDecoder();

// Record types (MS-XLSB 2.3.2)
const BRT_ROW_HDR = 0;
const BRT_CELL_BLANK = 1;
const BRT_CELL_RK = 2;
const BRT_CELL_ERROR = 3;
const BRT_CELL_BOOL = 4;
const BRT_CELL_REAL = 5;
const BRT_CELL_ST = 6;
const BRT_CELL_ISST = 7;
const BRT_FMLA_STRING = 8;
const BRT_FMLA_NUM = 9;
const BRT_FMLA_BOOL = 10;
const BRT_FMLA_ERROR = 11;
const BRT_SHORT_BLANK = 12;
const BRT_SHORT_RK = 13;
const BRT_SHORT_ERROR = 14;
const BRT_SHORT_BOOL = 15;
const BRT_SHORT_REAL = 16;
const BRT_SHORT_ST = 17;
const BRT_SHORT_ISST = 18;
const BRT_SST_ITEM = 19;
const BRT_BUNDLE_SH = 156;

/** Iterates records: calls visit(type, start, size) with offsets into bytes. */
function eachRecord(bytes, visit) {
  let p = 0;
  const end = bytes.length;
  while (p < end) {
    // Record type: 1-2 bytes, 7 bits each.
    let type = bytes[p] & 0x7f;
    if (bytes[p++] & 0x80) {
      type |= (bytes[p] & 0x7f) << 7;
      p += 1;
    }
    // Record size: 1-4 bytes, 7 bits each.
    let size = 0;
    for (let shift = 0; shift < 28; shift += 7) {
      const b = bytes[p++];
      size |= (b & 0x7f) << shift;
      if (!(b & 0x80)) break;
    }
    if (visit(type, p, size) === false) return;
    p += size;
  }
}

function wideString(bytes, view, offset) {
  const length = view.getUint32(offset, true);
  if (length === 0xffffffff) return { text: null, next: offset + 4 };
  const start = offset + 4;
  return { text: utf16.decode(bytes.subarray(start, start + length * 2)), next: start + length * 2 };
}

function rkValue(view, offset) {
  const rk = view.getUint32(offset, true);
  let value;
  if (rk & 2) {
    value = rk >> 2; // signed 30-bit integer
  } else {
    const buffer = new DataView(new ArrayBuffer(8));
    buffer.setUint32(4, rk & 0xfffffffc, true);
    buffer.setUint32(0, 0, true);
    value = buffer.getFloat64(0, true);
  }
  return rk & 1 ? value / 100 : value;
}

/** Number as plain text: no exponent for whole numbers (EANs, codes). */
export function numberToText(value) {
  if (!Number.isFinite(value)) return '';
  if (Number.isInteger(value)) {
    return Math.abs(value) < 1e21 ? value.toFixed(0) : String(value);
  }
  return String(Number(value.toPrecision(15)));
}

/**
 * Opens an .xlsb. Returns { sheets: [{ name }], readSheet(index, options) }.
 * readSheet options:
 *   limit    - stop after this many rows (for a quick preview)
 *   columns  - array of column indexes to keep (others are skipped; saves
 *              memory on large sheets). Omit to keep every column.
 * readSheet resolves to rows: arrays of cell text, blank rows left out.
 */
export async function openXlsb(input) {
  const zip = openZip(input);
  const workbook = await zip.read('xl/workbook.bin');
  if (!workbook) {
    throw new Error('This file is not an Excel Binary (.xlsb) workbook.');
  }

  const relsText = (await zip.read('xl/_rels/workbook.bin.rels')) || new Uint8Array();
  const rels = decoder.decode(relsText);
  const relTargets = new Map();
  for (const m of rels.matchAll(/<Relationship\b[^>]*>/g)) {
    const id = /Id="([^"]+)"/.exec(m[0])?.[1];
    const target = /Target="([^"]+)"/.exec(m[0])?.[1];
    if (id && target) {
      relTargets.set(id, target.startsWith('/') ? target.slice(1) : `xl/${target}`);
    }
  }

  const sheets = [];
  const wbView = new DataView(workbook.buffer, workbook.byteOffset, workbook.byteLength);
  eachRecord(workbook, (type, start) => {
    if (type !== BRT_BUNDLE_SH) return;
    const rel = wideString(workbook, wbView, start + 8);
    const name = wideString(workbook, wbView, rel.next);
    const path = relTargets.get(rel.text);
    if (path) sheets.push({ name: name.text || `Sheet${sheets.length + 1}`, path });
  });
  if (!sheets.length) throw new Error('No worksheets found in this .xlsb file.');

  let shared = null;
  async function sharedStrings() {
    if (shared) return shared;
    shared = [];
    const sst = await zip.read('xl/sharedStrings.bin');
    if (!sst) return shared;
    const view = new DataView(sst.buffer, sst.byteOffset, sst.byteLength);
    eachRecord(sst, (type, start) => {
      if (type === BRT_SST_ITEM) {
        // 1 byte of flags, then the text.
        shared.push(wideString(sst, view, start + 1).text || '');
      }
    });
    return shared;
  }

  // Unzipped sheets are kept so a preview and the full read unzip once.
  const sheetBytes = new Map();

  async function readSheet(index = 0, options = {}) {
    const sheet = sheets[index];
    if (!sheet) throw new Error('Sheet not found.');
    if (!sheetBytes.has(index)) sheetBytes.set(index, await zip.read(sheet.path));
    const bytes = sheetBytes.get(index);
    if (!bytes) throw new Error(`Sheet "${sheet.name}" is empty or missing.`);
    const strings = await sharedStrings();
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const limit = options.limit || Infinity;
    const keep = options.columns ? new Set(options.columns.map(Number)) : null;

    const rows = [];
    let cells = null;
    let lastColumn = -1;

    const flush = () => {
      if (cells && cells.some((cell) => cell !== undefined && cell !== '')) {
        for (let i = 0; i < cells.length; i += 1) if (cells[i] === undefined) cells[i] = '';
        rows.push(cells);
      }
    };

    eachRecord(bytes, (type, start) => {
      if (type === BRT_ROW_HDR) {
        flush();
        if (rows.length >= limit) return false;
        cells = [];
        lastColumn = -1;
        return undefined;
      }
      if (type > BRT_SST_ITEM - 1 || type < BRT_CELL_BLANK || !cells) return undefined;

      const short = type >= BRT_SHORT_BLANK;
      let column;
      let valueAt;
      if (short) {
        column = lastColumn + 1;
        valueAt = start + 4;
      } else {
        column = view.getUint32(start, true);
        valueAt = start + 8;
      }
      lastColumn = column;
      if (keep && !keep.has(column)) return undefined;

      let value = '';
      switch (type) {
        case BRT_CELL_RK:
        case BRT_SHORT_RK:
          value = numberToText(rkValue(view, valueAt));
          break;
        case BRT_CELL_REAL:
        case BRT_SHORT_REAL:
        case BRT_FMLA_NUM:
          value = numberToText(view.getFloat64(valueAt, true));
          break;
        case BRT_CELL_ST:
        case BRT_SHORT_ST:
        case BRT_FMLA_STRING:
          value = wideString(bytes, view, valueAt).text || '';
          break;
        case BRT_CELL_ISST:
        case BRT_SHORT_ISST:
          value = strings[view.getUint32(valueAt, true)] ?? '';
          break;
        case BRT_CELL_BOOL:
        case BRT_SHORT_BOOL:
        case BRT_FMLA_BOOL:
          value = bytes[valueAt] ? 'TRUE' : 'FALSE';
          break;
        case BRT_CELL_ERROR:
        case BRT_SHORT_ERROR:
        case BRT_FMLA_ERROR:
        case BRT_CELL_BLANK:
        case BRT_SHORT_BLANK:
        default:
          value = '';
      }
      cells[column] = String(value).trim();
      return undefined;
    });
    if (rows.length < limit) flush();
    return rows;
  }

  return { sheets: sheets.map((s) => ({ name: s.name })), readSheet };
}
